import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  sectionHeaderVariants,
  type SectionHeaderVariantProps,
} from './section-header.variants';
import { Brow } from '@/hen-ui/atomics/Brow';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Flex } from '@/hen-ui/composites/Flex';

export interface ActionButtonConfig
  extends Omit<ButtonProps, 'variant' | 'size'> {
  text: string;
}

export interface ButtonConfig extends Omit<ButtonProps, 'children'> {
  text: string;
}

export interface SectionHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    SectionHeaderVariantProps {
  /**
   * Heading size - required
   */
  size: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Main heading text - required
   */
  title: string;
  /**
   * Purple eyebrow text above title - optional
   */
  brow?: string;
  /**
   * Description paragraph below title - optional
   */
  subtext?: string;
  /**
   * Text link button config - renders on right side (left-aligned) or stacked (center)
   */
  actionButton?: ActionButtonConfig;
  /**
   * Primary CTA button config - renders below subtext
   */
  button?: ButtonConfig;
  /**
   * Text/icon color: default (neutral) or white
   */
  color?: 'default' | 'white';
}

/**
 * SectionHeader component following HEN Design System standards.
 *
 * A flexible section header that displays a heading with optional eyebrow, subtext,
 * action button, and CTA button. Supports both left-aligned and centered layouts.
 *
 * @example
 * // Basic left-aligned header
 * <SectionHeader
 *   size="h2"
 *   title="Section Title"
 * />
 *
 * @example
 * // Full left-aligned header with action button
 * <SectionHeader
 *   size="h2"
 *   align="left"
 *   brow="Eyebrow Text"
 *   title="Section Title"
 *   subtext="This is a description paragraph that provides context."
 *   actionButton={{ text: "View all", href: "/all" }}
 * />
 *
 * @example
 * // Centered header with CTA
 * <SectionHeader
 *   size="h1"
 *   align="center"
 *   brow="Welcome"
 *   title="Get Started Today"
 *   subtext="Join thousands of users already using our platform."
 *   button={{ text: "Sign Up", variant: "primaryFill" }}
 * />
 */
export const SectionHeader = React.forwardRef<
  HTMLDivElement,
  SectionHeaderProps
>(
  (
    {
      className,
      size,
      align = 'left',
      title,
      brow,
      subtext,
      color = 'default',
      actionButton,
      button,
      ...props
    },
    ref,
  ) => {
    const isLeftAligned = align === 'left';

    return (
      <Flex
        ref={ref}
        gap='xxs'
        className={cn(sectionHeaderVariants({ align }), className)}
        direction='column'
        justify='between'
        align={align === 'center' ? 'center' : 'start'}
        {...props}
      >
        {/* Brow */}
        {brow && (
          <Brow align={align} color={color}>
            {brow}
          </Brow>
        )}

        {/* Title row - for left-aligned, includes action button on same row */}
        {isLeftAligned && actionButton ? (
          <Flex
            direction='row'
            justify='between'
            align='center'
            className='w-full'
          >
            <Heading variant={size} color={color}>
              {title}
            </Heading>
            <Button variant='textOnly' size='sm' {...actionButton}>
              {actionButton.text}
            </Button>
          </Flex>
        ) : (
          <Heading variant={size} color={color}>
            {title}
          </Heading>
        )}

        {/* Subtext */}
        {subtext && (
          <Text variant='b4' color={color === 'white' ? 'white' : 'subtext'}>
            {subtext}
          </Text>
        )}

        {/* Primary CTA button */}
        {button && <Button {...button}>{button.text}</Button>}
      </Flex>
    );
  },
);

SectionHeader.displayName = 'SectionHeader';
